import { ConnectIntegrationCTA } from '@modules/integrationsV2/components/ConnectIntegrationCTA';
import { EditIntegrationCTA } from '@modules/integrationsV2/components/EditIntegrationCTA';
import { IntegrationActionsMenu } from '@modules/integrationsV2/components/IntegrationActionsMenu';
import { PreviewFeedbackCardCTA } from '@modules/integrationsV2/components/PreviewFeedbackCardCTA';
import { useIsIntegrationContextEnabled } from '@modules/integrationsV2/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getIsEditableIntegration,
  trackIntegrationPageEvent,
} from '@modules/integrationsV2/utils';
import { HelpOutlineRounded } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationType,
  IntegrationViewFragment,
} from 'generated/graphql';
import { FileUploader } from 'modules/ui/Settings/Integrations/FileUploader';
import { getInstructionsUrlForChannel } from 'modules/ui/Settings/Integrations/utils/getInstructionsUrlForChannel';
import { ReactNode } from 'react';
import useGetIntegrationsFormData from 'v2/core/repository/edge-server/integrations/useGetIntegrationsFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';

interface IntegrationSourceActionsProps {
  integrations: IntegrationViewFragment[];
  integrationSource: IntegrationSource;
}

export const IntegrationSourceActions = ({
  integrations,
  integrationSource,
}: IntegrationSourceActionsProps) => {
  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const { data: integrationsFormData, status: integrationsFormDataStatus } =
    useGetIntegrationsFormData({
      fetchPolicy: 'cache-first', // Because we have "cache-and-network" on the root component, we can use "cache-first" here.
    });

  const {
    integrationID,
    channel,
    integrationType,
    namespace,
    isDisabled,
    isIngestionInProgress,
    isSingleConnectionSource,
  } = integrationSource;

  const isFileUploadIntegration =
    channel.type === IntegrationChannelType.FileUpload;

  const isFeedbackIntegration = integrationType === IntegrationType.Feedback;

  const sourceDisplayName = `${channel.displayName} ${
    namespace ? ` - ${namespace}` : ''
  }`;

  const currentIntegration = isSingleConnectionSource
    ? integrations.find((integration) => integration.id === integrationID)
    : null;

  const isEmptyFileUploadIntegration =
    currentIntegration?.data.fileUpload?.isEmpty;

  const getSingleIntegrationViewActions = () => {
    const actions: ReactNode[] = [];

    const showUploadButton =
      isFileUploadIntegration &&
      !isEmptyFileUploadIntegration &&
      !isIngestionInProgress;

    if (showUploadButton) {
      actions.push(
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip arrow title="See instructions" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                window.open(
                  getInstructionsUrlForChannel({
                    type: IntegrationChannelType.FileUpload,
                  }),
                );
              }}
            >
              <HelpOutlineRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          {currentIntegration && <FileUploader id={currentIntegration.id} />}
        </Stack>,
      );
    }

    const showPreviewFeedbackCardButton =
      isFeedbackIntegration &&
      !isEmptyFileUploadIntegration &&
      !isIngestionInProgress;

    if (showPreviewFeedbackCardButton) {
      actions.push(
        <PreviewFeedbackCardCTA
          integrationSource={integrationSource}
          sourceDisplayName={sourceDisplayName}
        />,
      );
    }

    const isEditableIntegration =
      currentIntegration && !isIntegrationContextEnabled
        ? getIsEditableIntegration({
            channelType: channel.type,
            integrationData: currentIntegration.data,
            integrationType,
          })
        : isIntegrationContextEnabled;

    const integrationTypeEditable = integrationType === IntegrationType.Cohorts;

    if (
      currentIntegration &&
      (isEditableIntegration || integrationTypeEditable)
    ) {
      actions.push(
        <EditIntegrationCTA
          integration={currentIntegration}
          buttonVariant="outlined"
        />,
      );
    }

    if (currentIntegration && !isDisabled) {
      actions.push(
        <IntegrationActionsMenu
          integrationID={currentIntegration.id}
          channelType={currentIntegration.channel.type}
          integrationType={currentIntegration.integrationType}
          namespace={currentIntegration.namespace}
        />,
      );
    }

    return actions;
  };

  const getMultiIntegrationViewActions = () => {
    const actions: ReactNode[] = [];

    const selectedSourceFormData = integrationsFormData?.find(
      (integrationForm) => integrationForm.channel.type === channel.type,
    );

    actions.push(
      <ConnectIntegrationCTA
        isDrawerView={false}
        integrationChannel={channel}
        buttonLabel="Add New"
        oAuthURL={
          selectedSourceFormData?.__typename === 'OAuthFormData'
            ? selectedSourceFormData.oAuthURL
            : undefined
        }
        isDisabled={IsLoading(integrationsFormDataStatus)}
        integrationType={integrationType}
        onClickConnect={() => {
          trackIntegrationPageEvent({
            eventName: 'Integrations - Add New Button From Toolbar Clicked',
            integration: {
              integrationType: integrationType,
              channelType: channel.type,
              namespace: namespace,
            },
          });
        }}
      />,
    );

    if (isFeedbackIntegration) {
      actions.push(
        <PreviewFeedbackCardCTA
          integrationSource={integrationSource}
          sourceDisplayName={sourceDisplayName}
        />,
      );
    }

    return actions;
  };

  const actions = isSingleConnectionSource
    ? getSingleIntegrationViewActions()
    : getMultiIntegrationViewActions();

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {actions.map((action, index) => (
        <Box key={index}>{action}</Box>
      ))}
    </Stack>
  );
};
